import { useMemo } from 'react';

import { useSelector } from 'react-redux';
import { useLocation } from 'react-router';

import { filterFiltersByUrl } from '@/modules/analytics/constants';
import { selectFilters } from '@/modules/analytics/redux/selectors';
import { expandRatings } from '@/modules/analytics/utils/expandRatings';
import { selectAccountId } from '@/redux/session/selectors';
import { getAnalyticsRangeUTC } from '@/util/analytics';
import { parseFilter } from '@/util/util';

/** No conversation average can reach this, so it filters everything out. */
const UNREACHABLE_CSAT = 6;

/**
 * Shared hook that builds the GraphQL filter variables for all overview queries.
 * All three overview Hasura functions accept the same filter parameters,
 * so this centralises the variable construction.
 */
export const useOverviewVariables = (dateOverride?: { startDate: string; endDate: string }) => {
  const accountId = useSelector(selectAccountId);
  const filters = useSelector(selectFilters);
  const location = useLocation();

  const effectiveStartDate = dateOverride?.startDate ?? filters.startDate;
  const effectiveEndDate = dateOverride?.endDate ?? filters.endDate;

  const variables = useMemo(() => {
    const allowedFilters = filters.analytics.filter((filter) =>
      filterFiltersByUrl(filter.type, location.pathname)
    );

    // ratings → min/max CSAT scalars; minNumUserMessages → Int scalar.
    // Everything else flows through parseFilter as Postgres set literals.
    const ratingsFilter = allowedFilters.find((f) => f.type === 'ratings');
    const messagesFilter = allowedFilters.find((f) => f.type === 'minNumUserMessages');
    const otherFilters = allowedFilters.filter(
      (f) => f.type !== 'ratings' && f.type !== 'minNumUserMessages'
    );

    const vars = Object.assign(
      {},
      { ...getAnalyticsRangeUTC(effectiveStartDate, effectiveEndDate) },
      ...otherFilters.map((filter) => ({ [filter.type]: parseFilter(filter) }))
    );

    // The picker stores the threshold as a string ('2', '3', …) but the GQL
    // variable is declared as Int, so we coerce explicitly here.
    if (messagesFilter) {
      const raw = messagesFilter.minNumUserMessages;
      const parsed = raw == null ? null : Number(raw);
      vars.minNumUserMessages = parsed != null && !Number.isNaN(parsed) ? parsed : null;
    }

    // Overview matches an average, not a star, so the ticked stars become a
    // range and a gap in the set widens to cover it.
    if (ratingsFilter) {
      const { ratings, ratingsMode } = ratingsFilter;

      if (ratingsMode && ratingsMode !== 'in') {
        const threshold = Number(ratings?.[0]);
        if (!Number.isNaN(threshold)) {
          if (ratingsMode === 'lessThan') {
            vars.maxAvgCsatScore = threshold;
          } else {
            vars.minAvgCsatScore = threshold;
          }
        }
      } else {
        const stars = expandRatings(ratings, 'in');
        if (stars) {
          vars.minAvgCsatScore = stars.length > 0 ? Math.min(...stars) : UNREACHABLE_CSAT;
          vars.maxAvgCsatScore = stars.length > 0 ? Math.max(...stars) + 1 : UNREACHABLE_CSAT;
        }
      }
    }

    return vars;
  }, [filters.analytics, effectiveEndDate, effectiveStartDate, location.pathname]);

  const skip = !accountId || !filters.filtersLoaded;

  return { variables, skip };
};
